import React from 'react';
import { Loader2 } from 'lucide-react';
import {
    PENDING_TITLE,
    PENDING_BODY,
    PENDING_REFRESH_LABEL,
    PENDING_REFRESHING_LABEL,
    PENDING_FALLBACK,
} from '../lib/commercialCopy';

/** Delay before the manual refresh button becomes available. */
const REFRESH_BUTTON_DELAY_MS = 30_000;

interface Props {
    /** Reuses the existing access-status check (e.g. handleRunAudit). */
    onRefresh: () => void;
    /** True while a refresh is in flight — disables the button. */
    refreshing?: boolean;
}

/**
 * Calm, recovery-focused payment-pending card.
 *
 * Shown after the user returns from Stripe Checkout while the entitlement
 * webhook is still being processed. The existing 4-second auto-retry in
 * ReviewStep continues to run; this component layers a manual recovery
 * path on top for the (rare) cases where the webhook is slow.
 */
export const PaymentPendingCard: React.FC<Props> = ({ onRefresh, refreshing }) => {
    const [showRefresh, setShowRefresh] = React.useState(false);

    React.useEffect(() => {
        const timer = setTimeout(() => setShowRefresh(true), REFRESH_BUTTON_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="border border-[#E8D89A] bg-[#FAF3E0] p-6 text-center space-y-3">
            <Loader2 className="w-5 h-5 animate-spin text-[#7A5A00] mx-auto" />
            <p className="font-serif text-[18px] font-medium text-[#1A1A1A] m-0">{PENDING_TITLE}</p>
            <p className="font-serif italic text-[13px] leading-relaxed text-[#3F3D39] max-w-md mx-auto">
                {PENDING_BODY}
            </p>

            {showRefresh && (
                <div className="pt-2 space-y-2">
                    <button
                        onClick={onRefresh}
                        disabled={refreshing}
                        className="inline-flex items-center gap-2 bg-[#3730A3] text-white hover:bg-[#2E2780] px-5 py-3 text-[13px] font-medium tracking-[0.02em] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {refreshing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        {refreshing ? PENDING_REFRESHING_LABEL : PENDING_REFRESH_LABEL}
                    </button>
                    <p className="text-[11px] text-[#7A5A00] italic max-w-md mx-auto leading-relaxed">
                        {PENDING_FALLBACK}
                    </p>
                </div>
            )}
        </div>
    );
};
